/**
 * Project Name: Brainsie eCommerce v4
 * Hook: usePageContent
 * Description: Custom hook for fetching page content including sections and banners
 * Author: Brainsie Team
 * Date Created: October 30, 2025
 */

import { useState, useCallback } from "react";
import { getCMSSectionsByPage } from "services/cmsServices";
import { getPageById, postRecord } from "services/services";

const API_HOME_BANNERS_BY_URL = "banners/getbypageid";

export const usePageContent = () => {
  const [pageData, setPageData] = useState(null);
  const [sections, setSections] = useState([]);
  const [banners, setBanners] = useState([]);
  const [allContent, setAllContent] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchPageContent = useCallback(async (pageId) => {
    setLoading(true);
    setError(null);

    try {
      let page = null;

      // Fetch page data by ID
      if (pageId) {
        const response = await getPageById(pageId);
        if (response.status === 200 && response.data) {
          page = response.data;
        }
      }

      if (!page) {
        throw new Error("Page not found");
      }

      setPageData(page);

      // Fetch sections for this page
      let sectionsData = [];
      try {
        const sectionsResponse = await getCMSSectionsByPage(page.Page_Id);
        if (sectionsResponse.status === 200 && sectionsResponse.data) {
          sectionsData = sectionsResponse.data.map((section) => ({
            ...section,
            contentType: "section",
          }));
        }
      } catch (sectionsError) {
        console.warn("Error fetching sections:", sectionsError);
      }

      // Fetch banners for this page
      let bannersData = [];
      try {
        const bannersResponse = await postRecord(API_HOME_BANNERS_BY_URL, {
          Page_ID: 37,
        });
        if (bannersResponse.status === 200 && bannersResponse.data) {
          bannersData = bannersResponse.data.map((banner) => ({
            ...banner,
            contentType: "banner",
          }));
        }
      } catch (bannersError) {
        console.warn("Error fetching banners:", bannersError);
      }

      setSections(sectionsData);
      setBanners(bannersData);

      // Combine and sort all content by Display_Order
      const combinedContent = [...sectionsData, ...bannersData].sort(
        (a, b) => (a.Display_Order || 0) - (b.Display_Order || 0)
      );

      setAllContent(combinedContent);
    } catch (err) {
      console.error("Error fetching page content:", err);
      setError(err);
      setPageData(null);
      setSections([]);
      setBanners([]);
      setAllContent([]);
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    pageData,
    sections,
    banners,
    allContent,
    loading,
    error,
    fetchPageContent,
  };
};
